// src/components/pipeline/PipelineBoard.js — the investor pipeline board,
// shared by the CRM (MandateDetail) and the partner portal
// (PortalMandateDetail). Same columns, cards and drag-and-drop on both
// sides; each caller decides what a card shows, which moves are allowed and
// what a move does.
import { useState, useRef } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { PIPELINE_STAGES, PL_BADGE, PL_ACCENT } from '../../lib/pipelineStages'

const COL_WIDTH = 200
const COL_GAP = 10

// Board props:
//   entries     pipeline rows, each with pipeline_id and stage
//   renderCard  (entry) => card element, usually an <InvestorCardShell>
//   onMove      (entry, toStage, date) => void. date is YYYY-MM-DD when
//               askDate is on, otherwise undefined
//   canDrag     (entry) => bool, default all
//   canDrop     (entry, toStage) => bool, default all. A column the dragged
//               card can't enter is dimmed and the drop is ignored
//   askDate     true opens StageDateDialog on every move so the date the
//               stage was reached is entered deliberately (partner portal)
//   columnNote  (stage) => short text under a column header, or null
//   stages      column order, default PIPELINE_STAGES
export default function PipelineBoard({
  entries, renderCard, onMove,
  canDrag = () => true, canDrop = () => true,
  askDate = false, columnNote = () => null, stages = PIPELINE_STAGES,
}) {
  const [dragging, setDragging] = useState(null) // entry being dragged
  const [pending, setPending] = useState(null)   // { entry, toStage } awaiting a date

  // Top scrollbar mirrors the board's horizontal scroll: with many entries
  // the columns get tall enough that the native scrollbar at the bottom of
  // the content can be a long way down.
  const boardScrollRef = useRef(null)
  const topScrollRef = useRef(null)
  const syncingScrollRef = useRef(false)
  const boardWidth = stages.length * COL_WIDTH + (stages.length - 1) * COL_GAP

  function handleBoardScroll(e) {
    if (syncingScrollRef.current) { syncingScrollRef.current = false; return }
    if (topScrollRef.current) {
      syncingScrollRef.current = true
      topScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }
  function handleTopScroll(e) {
    if (syncingScrollRef.current) { syncingScrollRef.current = false; return }
    if (boardScrollRef.current) {
      syncingScrollRef.current = true
      boardScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }

  const byId = new Map(entries.map(e => [e.pipeline_id, e]))

  function handleDragStart(start) {
    setDragging(byId.get(start.draggableId) ?? null)
  }

  function handleDragEnd(result) {
    setDragging(null)
    const { destination, source, draggableId } = result
    if (!destination || destination.droppableId === source.droppableId) return
    const entry = byId.get(draggableId)
    const toStage = destination.droppableId
    if (!entry || !canDrop(entry, toStage)) return
    if (askDate) setPending({ entry, toStage })
    else onMove(entry, toStage)
  }

  const grouped = Object.fromEntries(stages.map(s => [s, entries.filter(e => e.stage === s)]))

  return (
    <>
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        style={{
          overflowX: 'auto', overflowY: 'hidden', marginBottom: 6,
          scrollbarWidth: 'thin', scrollbarColor: 'var(--border-light) transparent',
        }}
      >
        <div style={{ width: boardWidth, height: 1 }} />
      </div>
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div
          ref={boardScrollRef}
          onScroll={handleBoardScroll}
          style={{
            display: 'flex', gap: COL_GAP, overflowX: 'auto', paddingBottom: 12,
            alignItems: 'flex-start',
            scrollbarWidth: 'thin', scrollbarColor: 'var(--border-light) transparent',
          }}>

          {stages.map(stage => {
            const blocked = dragging && dragging.stage !== stage && !canDrop(dragging, stage)
            const note = columnNote(stage)
            return (
              <div key={stage} style={{ minWidth: COL_WIDTH, width: COL_WIDTH, flexShrink: 0,
                opacity: blocked ? 0.45 : 1, transition: 'opacity 0.15s' }}>
                {/* Column header */}
                <div style={{
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderBottom: 'none', borderRadius: '6px 6px 0 0',
                  borderTop: `3px solid ${PL_ACCENT[stage]}`,
                  padding: '8px 10px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                      {stage}
                    </span>
                    <span className="badge badge-gray" style={{ fontSize: 10, minWidth: 18, justifyContent: 'center' }}>
                      {grouped[stage].length}
                    </span>
                  </div>
                  {note && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{note}</div>
                  )}
                </div>
                {/* Cards with drop zone */}
                <Droppable droppableId={stage} isDropDisabled={!!blocked}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        background: snapshot.isDraggingOver ? 'var(--drop-target)' : 'var(--sidebar-bg)',
                        border: '1px solid var(--border)',
                        borderTop: 'none', borderRadius: '0 0 6px 6px',
                        padding: 7, display: 'flex', flexDirection: 'column', gap: 6,
                        minHeight: 100,
                        transition: 'background 0.15s',
                      }}
                    >
                      {grouped[stage].length === 0 && !snapshot.isDraggingOver && (
                        <div style={{ padding: '14px 8px', textAlign: 'center',
                          fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                          None
                        </div>
                      )}
                      {grouped[stage].map((e, index) => (
                        <Draggable key={e.pipeline_id} draggableId={e.pipeline_id} index={index}
                          isDragDisabled={!canDrag(e)}>
                          {(drag, dragSnapshot) => (
                            <div
                              ref={drag.innerRef}
                              {...drag.draggableProps}
                              {...drag.dragHandleProps}
                              style={{
                                borderRadius: 6,
                                opacity: dragSnapshot.isDragging ? 0.92 : 1,
                                boxShadow: dragSnapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.5)' : 'none',
                                ...drag.draggableProps.style,
                              }}
                            >
                              {renderCard(e)}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            )
          })}
        </div>
      </DragDropContext>

      {pending && (
        <StageDateDialog
          name={pending.entry.investor_contact?.name ?? pending.entry.investor_contact_name
            ?? pending.entry.investor_firm?.name ?? pending.entry.investor_firm_name}
          stage={pending.toStage}
          onCancel={() => setPending(null)}
          onSave={date => { onMove(pending.entry, pending.toStage, date); setPending(null) }}
        />
      )}
    </>
  )
}

// The card frame both sides use: name, firm, badges, then a footer the
// caller fills (commitment, since-date, action icons).
export function InvestorCardShell({ name, firm, stage, badges = null, footer = null, onClick, readOnly = false }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '12px 13px', cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.12s, background 0.12s',
      opacity: readOnly ? 0.75 : 1,
    }}
      onMouseEnter={e => { if (!onClick) return; e.currentTarget.style.borderColor = 'var(--border-light)'; e.currentTarget.style.background = 'var(--surface-raised)' }}
      onMouseLeave={e => { if (!onClick) return; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)' }}
    >
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
          {name || '—'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {firm || '—'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8, width: '100%' }}>
        <span className={`badge ${PL_BADGE[stage] ?? 'badge-gray'}`} style={{ whiteSpace: 'normal', textAlign: 'left', maxWidth: '100%' }}>{stage}</span>
        {badges}
      </div>

      {footer && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
          {footer}
        </div>
      )}
    </div>
  )
}

// Asks for the date a stage was reached, defaulting to today.
export function StageDateDialog({ name, stage, initialDate, onCancel, onSave, saving = false }) {
  const [date, setDate] = useState(initialDate ?? new Date().toLocaleDateString('en-CA'))
  return (
    <div onClick={onCancel} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: 20, width: 360, maxWidth: '100%',
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
          {name ? `Move ${name}` : 'Change stage'}
        </div>
        <div style={{ marginBottom: 14 }}>
          <span className={`badge ${PL_BADGE[stage] ?? 'badge-gray'}`}>{stage}</span>
        </div>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
          Date reached
        </label>
        <input type="date" className="input" value={date} onChange={e => setDate(e.target.value)}
          autoFocus style={{ width: '100%', marginBottom: 16 }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" disabled={!date || saving}
            onClick={() => onSave(date)}>Save</button>
        </div>
      </div>
    </div>
  )
}
